import { zodResolver } from "@hookform/resolvers/zod";
import { Download } from "lucide-react";
import { Info } from "lucide-react";
import React, { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useUpdateImportSourceMutation } from "../../../api/useUpdateImportSourceMutation";
import ClientConfigCard from "../components/ClientConfigCard";
import { useEditMode } from "../hooks/useEditMode";

import { useClientSchema } from "@/components/forms/schemas/clientSchema";
import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { RadioGroup, RadioGroupCard } from "@/components/ui/v2/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { getFeedFormatOptions } from "@/features/clients/feedFormatOptions";
import { handleProblemDetailsError } from "@/lib/helpers/handleProblemDetailsError";
import { useAuth } from "@/lib/hooks/useAuth";
import useToast from "@/lib/hooks/useToast";
import {
  ClientType,
  ClientTypeEnum,
} from "@/types/client/ClientType";

const SECTION_NAME = "import-source";

type ImportSourceCardProps = {
  client?: ClientType;
  clientId: string;
};

const ImportSourceCard: React.FC<ImportSourceCardProps> = ({
  client,
  clientId,
}) => {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const { showToast } = useToast();
  const { isEditMode, editMode, viewMode } = useEditMode(SECTION_NAME);
  const [originalValues, setOriginalValues] = useState<{
    clientType?: ClientTypeEnum;
    recruitNowUrl?: string;
    recruitNowApiKey?: string;
    feedUrl?: string;
    feedFormat?: string;
  }>({});

  const { combinedValidationSchema } = useClientSchema();
  const updateMutation = useUpdateImportSourceMutation(
    clientId,
    tenantId || ""
  );

  const methods = useForm<z.infer<typeof combinedValidationSchema>>({
    resolver: zodResolver(combinedValidationSchema),
    defaultValues: {
      clientType: client?.clientType || ClientTypeEnum.ATS,
      recruitNowUrl: client?.recruitNowUrl || "",
      recruitNowApiKey: client?.recruitNowApiKey || "",
      feedUrl: client?.feedUrl || "",
      feedFormat: client?.feedFormat || "DefaultRecruitNow",
    },
  });

  const { watch, getValues, setValue, resetField, setError, handleSubmit } = methods;

  const clientType = watch("clientType");
  const feedUrl = watch("feedUrl") || "";
  const feedFormat = watch("feedFormat") || "";

  const feedFormatOptions = getFeedFormatOptions();

  const typeOptions = [
    {
      label: t("common.ATS"),
      value: ClientTypeEnum.ATS,
      description: t("admin.client.addClient.clientType.ATSDescription"),
    },
    {
      label: t("common.feed"),
      value: ClientTypeEnum.FEED,
      description: t("admin.client.addClient.clientType.feedDescription"),
    },
  ];

  const handleTypeChange = (newType: ClientTypeEnum) => {
    setValue("clientType", newType);
    if (newType === ClientTypeEnum.ATS) {
      resetField("feedUrl");
      resetField("feedFormat");
    } else if (newType === ClientTypeEnum.FEED) {
      resetField("recruitNowUrl");
      resetField("recruitNowApiKey");
    }
  };

  const handleEdit = useCallback(() => {
    const current = getValues();
    const hasChanges =
      isEditMode &&
      (current.clientType !== originalValues.clientType ||
        current.recruitNowUrl !== originalValues.recruitNowUrl ||
        current.recruitNowApiKey !== originalValues.recruitNowApiKey ||
        current.feedUrl !== originalValues.feedUrl ||
        current.feedFormat !== originalValues.feedFormat);

    const canEdit = editMode(hasChanges, () => {
      setValue(
        "clientType",
        originalValues.clientType || client?.clientType || ClientTypeEnum.ATS
      );
      setValue(
        "recruitNowUrl",
        originalValues.recruitNowUrl || client?.recruitNowUrl || ""
      );
      setValue(
        "recruitNowApiKey",
        originalValues.recruitNowApiKey || client?.recruitNowApiKey || ""
      );
      setValue("feedUrl", originalValues.feedUrl || client?.feedUrl || "");
      setValue(
        "feedFormat",
        originalValues.feedFormat || client?.feedFormat || "DefaultRecruitNow"
      );
    });

    if (canEdit) {
      setOriginalValues({
        clientType: current.clientType,
        recruitNowUrl: current.recruitNowUrl,
        recruitNowApiKey: current.recruitNowApiKey,
        feedUrl: current.feedUrl,
        feedFormat: current.feedFormat,
      });
    }
  }, [editMode, isEditMode, getValues, setValue, originalValues, client]);

  const handleCancel = useCallback(() => {
    setValue(
      "clientType",
      originalValues.clientType || client?.clientType || ClientTypeEnum.ATS
    );
    setValue(
      "recruitNowUrl",
      originalValues.recruitNowUrl || client?.recruitNowUrl || ""
    );
    setValue(
      "recruitNowApiKey",
      originalValues.recruitNowApiKey || client?.recruitNowApiKey || ""
    );
    setValue("feedUrl", originalValues.feedUrl || client?.feedUrl || "");
    setValue(
      "feedFormat",
      originalValues.feedFormat || client?.feedFormat || "DefaultRecruitNow"
    );
    viewMode();
  }, [originalValues, client, setValue, viewMode]);

  const handleSave = useCallback(() => {
    const formData = getValues();
    const { clientType } = formData;

    const sectionData: {
      clientType?: ClientTypeEnum;
      recruitNowUrl?: string;
      recruitNowApiKey?: string;
      feedUrl?: string;
      feedFormat?: string;
      tenantId?: string | null;
    } = {
      clientType,
      tenantId: tenantId || undefined,
    };

    if (clientType === ClientTypeEnum.ATS) {
      // ATS credentials are no longer configured here - users must use integrations
      // Just set the clientType, no credentials
      sectionData.feedFormat = "";
      sectionData.feedUrl = "";
    } else if (clientType === ClientTypeEnum.FEED) {
      sectionData.feedUrl = formData.feedUrl;
      sectionData.feedFormat = formData.feedFormat;
      sectionData.recruitNowApiKey = "";
      sectionData.recruitNowUrl = "";
    }

    updateMutation.mutate(sectionData, {
      onSuccess: () => {
        setOriginalValues({
          clientType: formData.clientType,
          recruitNowUrl: undefined, // ATS credentials no longer stored here
          recruitNowApiKey: undefined, // ATS credentials no longer stored here
          feedUrl: sectionData.feedUrl,
          feedFormat: sectionData.feedFormat,
        });
        viewMode();
        showToast(
          t("admin.client.clientAction.success", { action: "updated" }),
          "success"
        );
      },
      onError: (error) => {
        handleProblemDetailsError({
          error: error as Parameters<typeof handleProblemDetailsError>[0]['error'],
          t,
          setError,
          formValues: getValues(),
          showToast,
        });
      },
    });
  }, [
    getValues,
    updateMutation,
    tenantId,
    t,
    showToast,
    setError,
    viewMode,
  ]);

  const onSubmitHandler = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSubmit(handleSave)(e);
    },
    [handleSubmit, handleSave]
  );

  const editContent = (
    <div className="space-y-4">
      <FormField
        control={methods.control}
        name="clientType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t("admin.client.addClient.clientType.clientType")}
            </FormLabel>
            <FormControl>
              <RadioGroup
                value={field.value}
                onValueChange={(value) => {
                  const typedValue = value as ClientTypeEnum;
                  field.onChange(typedValue);
                  handleTypeChange(typedValue);
                }}
              >
                {typeOptions.map((option) => (
                  <RadioGroupCard
                    key={option.value}
                    id={`clientType-${option.value}`}
                    value={option.value}
                    label={option.label}
                    description={option.description}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {clientType === ClientTypeEnum.ATS ? (
        <Alert variant="default" className="mt-2">
          <Info className="size-4" />
          <AlertDescription>
            {t(
              "admin.client.addClient.clientType.ATS.configureViaIntegrations",
              "Configure ATS on the Integrations page to sync jobs."
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <FormField
            control={methods.control}
            name="feedUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("admin.client.addClient.clientType.feed.feedUrl")}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    variant="outline"
                    value={field.value || ""}
                    placeholder={t(
                      "admin.client.addClient.clientType.feed.feedUrl"
                    )}
                    autoFocus={isEditMode && clientType === ClientTypeEnum.FEED}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={methods.control}
            name="feedFormat"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("admin.client.addClient.clientType.feed.feedFormat")}
                </FormLabel>
                <FormControl>
                  <Select
                    value={field.value || ""}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t(
                          "admin.client.addClient.clientType.feed.feedFormat"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {feedFormatOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  );

  const viewContent = (
    <div className="space-y-6">
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">
          {t("admin.client.addClient.clientType.clientType")}
        </label>
        <div className="text-base font-normal text-foreground">
          {clientType === ClientTypeEnum.ATS
            ? t("common.ATS")
            : t("common.feed")}
        </div>
      </div>
      {clientType === ClientTypeEnum.ATS ? (
        <Alert variant="default" className="mt-2">
          <Info className="size-4" />
          <AlertDescription>
            {t(
              "admin.client.addClient.clientType.ATS.configureViaIntegrations",
              "Configure ATS on the Integrations page to sync jobs."
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {feedUrl && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {t("admin.client.addClient.clientType.feed.feedUrl")}
              </label>
              <div className="text-base font-normal text-foreground">
                {feedUrl}
              </div>
            </div>
          )}
          {feedFormat && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {t("admin.client.addClient.clientType.feed.feedFormat")}
              </label>
              <div className="text-base font-normal text-foreground">
                {feedFormat}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <FormProvider {...methods}>
      <ClientConfigCard
        title={t("admin.client.editClientTabs.importSource")}
        icon={Download}
        isEditMode={isEditMode}
        isPending={updateMutation.isPending}
        onEdit={handleEdit}
        onCancel={handleCancel}
        editContent={editContent}
        onSubmit={onSubmitHandler}
      >
        {viewContent}
      </ClientConfigCard>
    </FormProvider>
  );
};

export default ImportSourceCard;
